/**
 * Ô tìm kiếm có gợi ý tự động — tin BĐS và địa điểm.
 *
 * Debounce 300ms; gọi propertyApi.getSuggestions khi query ≥ 2 ký tự.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { propertyApi } from '../../features/properties/api/propertyApi';
import { TEXT } from '../../shared/languages';
import { useCloseOnScroll } from '../../shared/hooks/useCloseOnScroll';
import { AnimatedPanel } from '../Common/AnimatedPanel';

const PropertyIcon = () => (
  <span className="suggestion-icon suggestion-icon--property" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5z" strokeLinejoin="round" />
    </svg>
  </span>
);

const LocationIcon = () => (
  <span className="suggestion-icon suggestion-icon--location" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10z" />
      <circle cx="12" cy="11" r="2.2" />
    </svg>
  </span>
);

/**
 * Autocomplete tìm kiếm BĐS và địa điểm.
 * @param {Object} props
 * @param {string} props.value
 * @param {Function} props.onChange
 * @param {Function} [props.onSelectProperty]
 * @param {Function} [props.onSelectLocation]
 * @param {string} [props.placeholder]
 * @param {string} [props.inputClassName]
 * @param {string} [props.panelClassName]
 * @param {boolean} [props.closeOnScroll]
 */
export const SearchAutocomplete = ({
  value,
  onChange,
  onSelectProperty,
  onSelectLocation,
  placeholder = TEXT.property.filters.searchPlaceholder,
  inputClassName = '',
  panelClassName = '',
  closeOnScroll = true,
}) => {
  const rootRef = useRef(null);
  const [suggestions, setSuggestions] = useState({ properties: [], locations: [] });
  const [isOpen, setIsOpen] = useState(false);
  const t = TEXT.search;

  useEffect(() => {
    const query = value.trim();

    if (query.length < 2) {
      setSuggestions({ properties: [], locations: [] });
      setIsOpen(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      propertyApi
        .getSuggestions(query)
        .then((response) => {
          setSuggestions(response.data.data);
          setIsOpen(true);
        })
        .catch(() => {
          setSuggestions({ properties: [], locations: [] });
          setIsOpen(false);
        });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [value]);

  const hasResults = suggestions.properties.length || suggestions.locations.length;
  const isPanelOpen = isOpen && hasResults;

  const closePanel = useCallback(() => setIsOpen(false), []);

  useCloseOnScroll(closeOnScroll && isPanelOpen, closePanel);

  useEffect(() => {
    if (!isPanelOpen) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
        rootRef.current.querySelector('input')?.blur();
      }
    };

    document.addEventListener('mousedown', handlePointerDownOutside);

    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside);
    };
  }, [isPanelOpen]);

  return (
    <div className="search-autocomplete" ref={rootRef}>
      <input
        className={inputClassName}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => hasResults && setIsOpen(true)}
        onBlur={() => window.setTimeout(() => setIsOpen(false), 150)}
        placeholder={placeholder}
      />
      <AnimatedPanel
        isOpen={isPanelOpen}
        className={`search-autocomplete-panel animated-panel animated-panel--left ${panelClassName}`.trim()}
      >
          {suggestions.properties.length > 0 && (
            <div className="search-autocomplete-group">
              <p className="search-autocomplete-heading">{t.propertySuggestions}</p>
              <div className="search-autocomplete-list">
                {suggestions.properties.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="search-autocomplete-item"
                    onMouseDown={() => {
                      onSelectProperty?.(item);
                      setIsOpen(false);
                    }}
                  >
                    <PropertyIcon />
                    <span className="search-autocomplete-copy">
                      <strong>{item.title}</strong>
                      {item.label && <span>{item.label}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {suggestions.locations.length > 0 && (
            <div className="search-autocomplete-group">
              <p className="search-autocomplete-heading">{t.locationSuggestions}</p>
              <div className="search-autocomplete-list">
                {suggestions.locations.map((item) => (
                  <button
                    key={`${item.province}-${item.district}-${item.ward}`}
                    type="button"
                    className="search-autocomplete-item"
                    onMouseDown={() => {
                      onSelectLocation?.(item);
                      setIsOpen(false);
                    }}
                  >
                    <LocationIcon />
                    <span className="search-autocomplete-copy">
                      <strong>{item.label}</strong>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
      </AnimatedPanel>
    </div>
  );
};
